import { CircularProgress } from "@mui/material";
import { type ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  ClassPostObjType,
  PredictionObject,
} from "../../../services/api/instances/classificatorApi/types";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { AppButton } from "../../../shared/components/AppButton";
import { AppInputBase } from "../../../shared/components/AppInputBase";
import { AppUnderlinedTitle } from "../../../shared/components/AppUnderlinedTitle";
import { useClassifier } from "../hooks/use-classifier";
import {
  selectClassifierResults,
  selectSelectedClassifier,
} from "../redux/selectors";
import type { ChartEntry } from "./MyClassChart";
import { MyClassChart } from "./MyClassChart";
import styles from "./styles.module.css";
import { CopyButton } from "../../../common/Components/Buttons/CopyButton";

interface Props {
  data: PredictionObject | null;
  leftData: ClassPostObjType["labels"];
  requestBody: () => string;
  getRequestBodyForCopy: () => string;
  isLoading: boolean;
  onClassification: () => void;
}

export const ClassRightContainer: React.FC<Props> = ({
  data,
  leftData,
  requestBody,
  getRequestBodyForCopy,
  isLoading,
  onClassification,
}) => {
  const selectedClassifier = useAppSelector(selectSelectedClassifier);
  const classifierResults = useAppSelector(selectClassifierResults);
  const { handleInputFieldChange } = useClassifier();
  const [isDisabled, setIsDisabled] = useState(true);
  const { t } = useTranslation();

  const checkVariantUniqueness = (labels: ClassPostObjType["labels"] = []) => {
    for (let i = 0; i < labels.length; i++) {
      for (let j = i + 1; j < labels.length; j++) {
        if (labels[i].label === labels[j].label) {
          return false;
        }
      }
    }
    return true;
  };

  useEffect(() => {
    const validVariants =
      leftData?.filter((card) => card.label.trim() !== "") || [];
    const areVariantsUnique = checkVariantUniqueness(leftData);

    if (
      selectedClassifier?.value?.classifier_object?.text_request &&
      validVariants.length >= 2 &&
      areVariantsUnique
    ) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [selectedClassifier?.value?.classifier_object, leftData]);

  let chartData: ChartEntry[] = [];

  if (data) {
    chartData = data.predictions
      .filter((item) => item.label)
      .map((item) => {
        return {
          label: item.label,
          value: item.confidence,
        };
      });
  }

  const chartRenderLogic = (isLoading: boolean) => {
    if (isLoading) {
      return (
        <div className={styles["loader-container"]}>
          <CircularProgress size={50} color="success" />
        </div>
      );
    }
    if (!isLoading && classifierResults)
      return <MyClassChart data={chartData} />;
  };

  return (
    <div className={styles["right-container"]}>
      <AppUnderlinedTitle
        className={styles.title}
        leftContent={t("classification.instruction_title")}
      />
      <AppInputBase
        small
        minRows={4}
        rows={undefined}
        placeholder={t("classification.instruction_placeholder")}
        className={styles["text-area"]}
        value={selectedClassifier?.value?.classifier_object?.prefix}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          handleInputFieldChange(event, "prefix")
        }
      />
      <AppUnderlinedTitle
        className={styles.title}
        leftContent={t("classification.classification_title")}
      />
      <AppInputBase
        small
        minRows={4}
        rows={undefined}
        placeholder={t("classification.classification_placeholder")}
        value={selectedClassifier?.value?.classifier_object?.text_request}
        className={styles["text-area"]}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          handleInputFieldChange(event, "text_request")
        }
      />
      <AppButton
        className={styles.button}
        buttonType="outlined"
        onClick={onClassification}
        disabled={isDisabled || isLoading}
      >
        {t("classification.classify_button")}
      </AppButton>
      <div className={styles["chart-container"]}>
        {chartRenderLogic(isLoading)}
      </div>
      <div className={styles["wrapper"]}>
        <pre>{requestBody()}</pre>
        <CopyButton copyContent={getRequestBodyForCopy()} position="absolute" />
      </div>
    </div>
  );
};
